import {
  Badge,
  Button,
  ColumnGroup,
  CustomColumnDef,
  Dropdown,
  Icons,
  TruncatedText,
} from '@commenda/kakashi';
import { getClientInfoFromCookie } from '@commenda/auth';
import { COMMENDA_IO_EMAIL_DOMAIN, compareDatesChronologically } from '@commenda/utils';

import { IndirectTaxTransactionHelper } from '../helpers';
import {
  TIndirectTaxTransaction,
  TIndirectTaxTransactionRegion,
  TIndirectTaxTransactionStatus,
} from '../types';

type Column = CustomColumnDef<TIndirectTaxTransaction, unknown>;

const transactionIdColumn: Column = {
  accessorKey: 'id',
  header: 'Transaction ID',
  title: 'Transaction ID',
  enableSorting: true,
  sort: {
    shouldSort: true,
    sortingFn: (a: string, b: string) => a.localeCompare(b),
    ui: {
      asc: { columnNode: 'A to Z', globalNode: 'Transaction ID - A to Z' },
      desc: { columnNode: 'Z to A', globalNode: 'Transaction ID - Z to A' },
    },
  },
  cell: ({ row }) => (
    <span className="txt-small-medium text-text-primary">
      {row.original.id}
    </span>
  ),
};

const transactionTypeColumn: Column = {
  accessorKey: 'transactionType',
  header: 'Document type',
  title: 'Document type',
  enableSorting: false,
  cell: ({ row }) => {
    const type = row.original.transactionType;
    return (
      <Badge variant={IndirectTaxTransactionHelper.getTransactionTypeBadgeConfig(type).variant} size="sm">
        {IndirectTaxTransactionHelper.getTransactionTypeBadgeConfig(type).label}
      </Badge>
    );
  },
};

const statusColumn: Column = {
  accessorKey: 'status',
  header: 'Status',
  title: 'Status',
  enableSorting: false,
  cell: ({ row }) => {
    const config = IndirectTaxTransactionHelper.getTransactionStatusBadgeConfig(
      row.original.status
    );
    return (
      <Badge variant={config.variant} size="sm">
        {config.label}
      </Badge>
    );
  },
};

const transactionDateColumn: Column = {
  accessorKey: 'date',
  header: 'Transaction date',
  title: 'Transaction date',
  enableSorting: true,
  sort: {
    shouldSort: true,
    sortingFn: compareDatesChronologically,
    ui: {
      asc: {
        columnNode: 'Oldest to newest',
        globalNode: 'Transaction date - Oldest to newest',
      },
      desc: {
        columnNode: 'Newest to oldest',
        globalNode: 'Transaction date - Newest to oldest',
      },
    },
  },
  cell: ({ row }) => (
    <span className="txt-small-regular text-text-primary">
      {row.original.date}
    </span>
  ),
};

const reportingDateColumn: Column = {
  accessorKey: 'reportingDate',
  header: 'Reporting date',
  title: 'Reporting date',
  enableSorting: true,
  sort: {
    shouldSort: true,
    sortingFn: compareDatesChronologically,
    ui: {
      asc: {
        columnNode: 'Oldest to newest',
        globalNode: 'Reporting date - Oldest to newest',
      },
      desc: {
        columnNode: 'Newest to oldest',
        globalNode: 'Reporting date - Newest to oldest',
      },
    },
  },
  cell: ({ row }) => {
    const value = row.original.reportingDate;
    if (!value) {
      return (
        <Badge variant="warning" size="sm">
          Missing
        </Badge>
      );
    }
    return <span className="txt-small-regular text-text-primary">{value}</span>;
  },
};

const sourceColumn: Column = {
  accessorKey: 'source',
  header: 'Source',
  title: 'Source',
  enableSorting: false,
  cell: ({ row }) => (
    <Badge variant="gray" size="sm">
      {row.original.source}
    </Badge>
  ),
};

const sourcePlatformIdColumn: Column = {
  accessorKey: 'sourcePlatformId',
  header: 'Source platform ID',
  title: 'Source platform ID',
  enableSorting: false,
  cell: ({ row }) => {
    const value = row.original.sourcePlatformId;
    if (!value) return <span className="text-text-quaternary">—</span>;
    return (
      <Badge variant="gray" size="sm">
        {value}
      </Badge>
    );
  },
};

const invoiceNumberColumn: Column = {
  accessorKey: 'invoiceNumber',
  header: 'Document ID',
  title: 'Document ID',
  enableSorting: true,
  width: 240,
  sort: {
    shouldSort: true,
    sortingFn: (a: string, b: string) => a.localeCompare(b),
    ui: {
      asc: { columnNode: 'A to Z', globalNode: 'Document ID - A to Z' },
      desc: { columnNode: 'Z to A', globalNode: 'Document ID - Z to A' },
    },
  },
  cell: ({ row }) => {
    const value = row.original.invoiceNumber;
    if (!value) return <span className="text-text-quaternary">—</span>;
    return (
      <TruncatedText tooltipContent={value} >{value}</TruncatedText>
    );
  },
};

const customerNameColumn: Column = {
  accessorKey: 'customerName',
  header: 'Customer',
  title: 'Customer',
  enableSorting: false,
  cell: ({ row }) => {
    const name = row.original.customer?.name;
    if (!name) return <span className="text-text-quaternary">—</span>;
    return (
      <span className="txt-small-medium text-text-brand-primary truncate max-w-48">
        {name}
      </span>
    );
  },
};

function getCustomerNameColumn(
  onCustomerClick?: (customerId: string) => void
): Column {
  if (!onCustomerClick) return customerNameColumn;
  return {
    ...customerNameColumn,
    cell: ({ row }) => {
      const name = row.original.customer?.name;
      const customerId = row.original.customer?.id;
      if (!name) return <span className="text-text-quaternary">—</span>;
      if (!customerId) {
        return (
          <span className="txt-small-medium text-text-brand-primary truncate max-w-48">
            {name}
          </span>
        );
      }
      return (
        <span
          onClick={(e) => {
            e.stopPropagation();
            onCustomerClick(customerId);
          }}
          className="txt-small-medium text-text-brand-primary truncate max-w-48 hover:underline text-left"
        >
          {name}
        </span>
      );
    },
  };
}

const customerEmailColumn: Column = {
  accessorKey: 'customerEmail',
  header: 'Customer email',
  title: 'Customer email',
  enableSorting: false,
  cell: ({ row }) => {
    const email = row.original.customer?.email;
    if (!email) return <span className="text-text-quaternary">—</span>;
    return (
      <span className="txt-small-regular text-text-secondary truncate max-w-64">
        {email}
      </span>
    );
  },
};

const effectiveTaxRateColumn: Column = {
  accessorKey: 'effectiveTaxRate',
  header: 'Effective tax rate',
  title: 'Effective tax rate',
  enableSorting: false,
  align: 'right',
  headerClassName: 'w-full text-right',
  cell: ({ row }) => (
    <span className="txt-small-regular text-text-primary">
      {row.original.effectiveTaxRate}
    </span>
  ),
};

const effectiveVatRateColumn: Column = {
  accessorKey: 'effectiveVatRate',
  header: 'Effective VAT rate',
  title: 'Effective VAT rate',
  enableSorting: false,
  align: 'right',
  headerClassName: 'w-full text-right',
  cell: ({ row }) => (
    <span className="txt-small-regular text-text-primary">
      {row.original.effectiveVatRate ?? '—'}
    </span>
  ),
};

const jurisdictionNameColumn: Column = {
  accessorKey: 'jurisdictionName',
  header: 'Destination state',
  title: 'Destination state',
  enableSorting: false,
  cell: ({ row }) => (
    <span className="txt-small-regular text-text-primary">
      {row.original.jurisdictionName || '—'}
    </span>
  ),
};

const destinationCountryColumn: Column = {
  accessorKey: 'destinationCountryName',
  header: 'Destination country',
  title: 'Destination country',
  enableSorting: false,
  cell: ({ row }) => (
    <span className="txt-small-regular text-text-primary">
      {row.original.destinationCountryName || '—'}
    </span>
  ),
};

const originCountryColumn: Column = {
  accessorKey: 'originCountryName',
  header: 'Origin country',
  title: 'Origin country',
  enableSorting: false,
  cell: ({ row }) => (
    <span className="txt-small-regular text-text-primary">
      {row.original.originCountryName || '—'}
    </span>
  ),
};

const marketplaceColumn: Column = {
  accessorKey: 'marketplace',
  header: 'Marketplace',
  title: 'Marketplace',
  enableSorting: false,
  cell: ({ row }) => {
    const value = row.original.marketplace;
    if (!value) return <span className="text-text-quaternary">—</span>;
    return (
      <Badge variant="gray" size="sm">
        {value}
      </Badge>
    );
  },
};

const supplyTypeColumn: Column = {
  accessorKey: 'supplyType',
  header: 'Supply type',
  title: 'Supply type',
  enableSorting: false,
  cell: ({ row }) => {
    const value = row.original.supplyType;
    if (!value) return <span className="text-text-quaternary">—</span>;
    return (
      <Badge variant="gray" size="sm">
        {IndirectTaxTransactionHelper.getSupplyTypeName(value)}
      </Badge>
    );
  },
};


function createAmountColumn(
  key: string,
  header: string,
  accessor: keyof TIndirectTaxTransaction
): Column {
  return {
    accessorKey: key,
    header,
    title: header,
    enableSorting: true,
    align: 'right',
    headerClassName: 'w-full text-right',
    sort: {
      shouldSort: true,
      sortingFn: (a: string, b: string) => (Number(a) || 0) - (Number(b) || 0),
      ui: {
        asc: {
          columnNode: 'Low to high',
          globalNode: `${header} - Low to high`,
        },
        desc: {
          columnNode: 'High to low',
          globalNode: `${header} - High to low`,
        },
      },
    },
    cell: ({ row }) => {
      const value = row.original[accessor];
      const numVal = typeof value === 'number' ? value : 0;
      const currency = IndirectTaxTransactionHelper.getCurrencySymbol(
        row.original.currency
      );
      return (
        <div className="flex items-center justify-end gap-xs">
          <span className="txt-small-regular text-text-quaternary">
            {currency}
          </span>
          <span className="txt-small-regular text-text-primary">
            {IndirectTaxTransactionHelper.formatCurrency(numVal)}
          </span>
        </div>
      );
    },
  };
}

function getActionsColumn(
  onRowAction?: (transaction: TIndirectTaxTransaction, action: TIndirectTaxTransactionStatus) => void
): Column {
  return {
    accessorKey: 'actions',
    header: '',
    title: '',
    width: 65,
    showInSelector: false,
    enableSorting: false,
    cell: ({ row }: { row: { original: TIndirectTaxTransaction } }) => {
      const tx = row.original;
      const isUncommitted = tx.status === TIndirectTaxTransactionStatus.UNCOMMITTED;
      const isCommitted = tx.status === TIndirectTaxTransactionStatus.COMMITTED;
      const isCommendaUser = getClientInfoFromCookie()
        ?.user?.email?.toLowerCase()
        ?.includes(COMMENDA_IO_EMAIL_DOMAIN);
      const hasActions = (isUncommitted || isCommitted) && isCommendaUser;


      if (!onRowAction) {
        return (
          <button
            className="flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Icons.DotsVertical className="size-4 text-text-quaternary" />
          </button>
        );
      }

      if (!hasActions) {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{
                groups: [
                  {
                    items: [
                      {
                        label: 'No actions available',
                        onClick: () => { },
                      },
                    ],
                  },
                ],
              }}
              align="end"
            >
              <Button variant="tertiary" size="sm">
                <Icons.DotsVertical className="size-4 text-text-quaternary" />
              </Button>
            </Dropdown>
          </div>
        );
      }

      const internalBadge = (
        <Badge variant="gray" size="sm">
          Internal
        </Badge>
      );

      const items: {
        icon: React.ElementType;
        label: string;
        onClick: () => void;
        shortcut?: React.ReactNode;
      }[] = [];

      if (isUncommitted) {
        items.push({
          icon: Icons.CheckCircle,
          label: 'Mark as to be filed',
          shortcut: internalBadge,
          onClick: () => onRowAction(tx, TIndirectTaxTransactionStatus.COMMITTED),
        });
        items.push({
          icon: Icons.AlertCircle,
          label: 'Mark as void',
          shortcut: internalBadge,
          onClick: () => onRowAction(tx, TIndirectTaxTransactionStatus.VOIDED),
        });
      }

      if (isCommitted) {
        items.push({
          icon: Icons.Lock01,
          label: 'Mark as locked',
          shortcut: internalBadge,
          onClick: () => onRowAction(tx, TIndirectTaxTransactionStatus.LOCKED),
        });
        items.push({
          icon: Icons.AlertCircle,
          label: 'Mark as void',
          shortcut: internalBadge,
          onClick: () => onRowAction(tx, TIndirectTaxTransactionStatus.VOIDED),
        });
      }

      return (
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown
            // Safe: Icons.* components are compatible with Dropdown's icon type at runtime
            menu={{ groups: [{ items: items as never[] }] }}
            align="end"
          >
            <Button variant="tertiary" size="sm">
              <Icons.DotsVertical className="size-4 text-text-quaternary" />
            </Button>
          </Dropdown>
        </div>
      );
    },
  };
}

const GENERIC_REGION_COLUMNS: Column[] = [
  transactionTypeColumn,
  destinationCountryColumn,
  originCountryColumn,
  jurisdictionNameColumn,
  transactionDateColumn,
  reportingDateColumn,
  sourcePlatformIdColumn,
  transactionIdColumn,
  customerNameColumn,
  customerEmailColumn,
  createAmountColumn('amount', 'Amount', 'amount'),
  createAmountColumn('taxCollected', 'Tax', 'taxCollected'),
  createAmountColumn('total', 'Total', 'total'),
  effectiveTaxRateColumn,
  statusColumn,
  sourceColumn
];

const REGION_COLUMNS: Record<TIndirectTaxTransactionRegion, Column[]> = {
  [TIndirectTaxTransactionRegion.ALL_REGIONS]: GENERIC_REGION_COLUMNS,
  [TIndirectTaxTransactionRegion.ASIA_PACIFIC]: GENERIC_REGION_COLUMNS,
  [TIndirectTaxTransactionRegion.MIDDLE_EAST]: GENERIC_REGION_COLUMNS,
  [TIndirectTaxTransactionRegion.LATIN_AMERICA]: GENERIC_REGION_COLUMNS,
  [TIndirectTaxTransactionRegion.UNSUPPORTED]: GENERIC_REGION_COLUMNS,
  [TIndirectTaxTransactionRegion.UNITED_STATES]: [
    transactionTypeColumn,
    jurisdictionNameColumn,
    transactionDateColumn,
    reportingDateColumn,
    marketplaceColumn,
    sourcePlatformIdColumn,
    transactionIdColumn,
    customerNameColumn,
    customerEmailColumn,
    createAmountColumn('amount', 'Amount', 'amount'),
    createAmountColumn('taxCollected', 'Tax collected', 'taxCollected'),
    createAmountColumn('total', 'Total', 'total'),
    effectiveTaxRateColumn,
    statusColumn,
    sourceColumn
  ],
  [TIndirectTaxTransactionRegion.EUROPEAN_UNION]: [
    transactionTypeColumn,
    supplyTypeColumn,
    destinationCountryColumn,
    originCountryColumn,
    transactionDateColumn,
    reportingDateColumn,
    transactionIdColumn,
    customerNameColumn,
    customerEmailColumn,
    createAmountColumn('taxableAmount', 'Taxable amount', 'taxableAmount'),
    createAmountColumn('vat', 'VAT', 'vat'),
    createAmountColumn('grossAmount', 'Gross amount', 'grossAmount'),
    effectiveVatRateColumn,
    statusColumn,
    sourceColumn,
    sourcePlatformIdColumn
  ],
  [TIndirectTaxTransactionRegion.UNITED_KINGDOM]: [
    transactionTypeColumn,
    supplyTypeColumn,
    jurisdictionNameColumn,
    destinationCountryColumn,
    originCountryColumn,
    transactionDateColumn,
    reportingDateColumn,
    transactionIdColumn,
    customerNameColumn,
    customerEmailColumn,
    createAmountColumn('taxableAmount', 'Taxable amount', 'taxableAmount'),
    createAmountColumn('vat', 'VAT', 'vat'),
    createAmountColumn('grossAmount', 'Gross amount', 'grossAmount'),
    effectiveVatRateColumn,
    statusColumn,
    sourceColumn,
    sourcePlatformIdColumn
  ],
  [TIndirectTaxTransactionRegion.CANADA]: [
    transactionTypeColumn,
    jurisdictionNameColumn,
    destinationCountryColumn,
    originCountryColumn,
    transactionDateColumn,
    reportingDateColumn,
    transactionIdColumn,
    customerNameColumn,
    customerEmailColumn,
    createAmountColumn('amount', 'Amount', 'amount'),
    createAmountColumn('taxCollected', 'Tax collected', 'taxCollected'),
    createAmountColumn('total', 'Total', 'total'),
    effectiveTaxRateColumn,
    statusColumn,
    sourceColumn,
    sourcePlatformIdColumn
  ],
};

export function getColumnGroupsForRegion(
  region: TIndirectTaxTransactionRegion,
  onRowAction?: (transaction: TIndirectTaxTransaction, action: TIndirectTaxTransactionStatus) => void,
  options?: { includeActions?: boolean; onCustomerClick?: (customerId: string) => void }
): ColumnGroup<TIndirectTaxTransaction, unknown>[] {
  const includeActions = options?.includeActions ?? true;
  const customerColumn = getCustomerNameColumn(options?.onCustomerClick);
  const mainColumns = REGION_COLUMNS[region].map((col) =>
    col === customerNameColumn ? customerColumn : col
  );
  const groups: ColumnGroup<TIndirectTaxTransaction, unknown>[] = [
    {
      id: 'id',
      sticky: 'left',
      columns: [invoiceNumberColumn],
    },
    { id: 'main', columns: mainColumns },
  ];
  if (includeActions) {
    groups.push({
      id: 'actions',
      sticky: 'right',
      columns: [getActionsColumn(onRowAction)],
    });
  }
  return groups;
}

const DEFAULT_HIDDEN_COLUMNS = [
  'id',
  'reportingDate',
  'customerEmail',
  'sourcePlatformId',
  'customerName',
];

const GENERIC_REGION_HIDDEN_COLUMNS: string[] = [
  ...DEFAULT_HIDDEN_COLUMNS,
  'jurisdictionName',
  'originCountryName',
];

const REGION_DEFAULT_HIDDEN_COLUMNS: Record<
  TIndirectTaxTransactionRegion,
  string[]
> = {
  [TIndirectTaxTransactionRegion.ALL_REGIONS]: GENERIC_REGION_HIDDEN_COLUMNS,
  [TIndirectTaxTransactionRegion.ASIA_PACIFIC]: GENERIC_REGION_HIDDEN_COLUMNS,
  [TIndirectTaxTransactionRegion.MIDDLE_EAST]: GENERIC_REGION_HIDDEN_COLUMNS,
  [TIndirectTaxTransactionRegion.LATIN_AMERICA]: GENERIC_REGION_HIDDEN_COLUMNS,
  [TIndirectTaxTransactionRegion.UNSUPPORTED]: GENERIC_REGION_HIDDEN_COLUMNS,
  [TIndirectTaxTransactionRegion.UNITED_STATES]: [...DEFAULT_HIDDEN_COLUMNS, 'marketplace'],
  [TIndirectTaxTransactionRegion.EUROPEAN_UNION]: [...DEFAULT_HIDDEN_COLUMNS],
  [TIndirectTaxTransactionRegion.UNITED_KINGDOM]: [...DEFAULT_HIDDEN_COLUMNS, 'originCountryName'],
  [TIndirectTaxTransactionRegion.CANADA]: [...DEFAULT_HIDDEN_COLUMNS, 'originCountryName'],
};

export function getDefaultVisibleColumnIdsForRegion(params:
  { region: TIndirectTaxTransactionRegion }
): string[] | undefined {
  const { region } = params
  const hidden = REGION_DEFAULT_HIDDEN_COLUMNS[region];
  if (!hidden.length) return undefined;
  return REGION_COLUMNS[region]
    .map((col) => (col as { accessorKey?: string }).accessorKey)
    .filter((id): id is string => !!id && !hidden.includes(id));
}
